import axios from 'axios';
import { axiosClientAdmin, admin_api } from '@/lib/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  EdgeLinkBridge,
  EdgeLinkBridgeDetail,
  ListEdgeLinksResponse,
  CreateBridgeDto,
  UpdateBridgeDto,
  BridgeRouteOption,
  BridgeRoute,
  CreateRouteDto,
  UpdateRouteDto,
  EdgeLinkStatusResponse,
  EdgeLinkSiteDto,
  EdgeLinkSiteWithDevicesDto,
  ConnectedAssetsResponse,
  ConnectedDeviceDto,
  ShareLinkResponse,
  PublicBridgeStatus,
} from './types';

// Plain axios instance without auth interceptors for public endpoints
const publicClient = axios.create({ baseURL: admin_api });

// === API Functions ===

async function listBridges(page = 1, pageSize = 20): Promise<ListEdgeLinksResponse> {
  const response = await axiosClientAdmin.get<ListEdgeLinksResponse>('/EdgeLinks/bridges', {
    params: { page, pageSize },
  });
  return response.data;
}

async function getBridgeById(id: string): Promise<EdgeLinkBridgeDetail> {
  const response = await axiosClientAdmin.get<EdgeLinkBridgeDetail>(`/EdgeLinks/bridges/${id}`);
  return response.data;
}

async function getBridgeStatus(id: string): Promise<EdgeLinkStatusResponse> {
  const response = await axiosClientAdmin.get<EdgeLinkStatusResponse>(`/EdgeLinks/bridges/${id}/status`);
  return response.data;
}

async function createBridge(data: CreateBridgeDto): Promise<EdgeLinkBridge> {
  const response = await axiosClientAdmin.post<EdgeLinkBridge>('/EdgeLinks/bridges', data);
  return response.data;
}

async function updateBridge(id: string, data: UpdateBridgeDto): Promise<EdgeLinkBridgeDetail> {
  const response = await axiosClientAdmin.put<EdgeLinkBridgeDetail>(`/EdgeLinks/bridges/${id}`, data);
  return response.data;
}

async function deleteBridge(id: string): Promise<void> {
  await axiosClientAdmin.delete(`/EdgeLinks/bridges/${id}`);
}

// === Query Keys ===

export const edgeLinkQueryKeys = {
  all: ['edgelink'] as const,
  list: (page?: number, pageSize?: number) => [...edgeLinkQueryKeys.all, 'list', { page, pageSize }] as const,
  detail: (id: string) => [...edgeLinkQueryKeys.all, 'detail', id] as const,
  status: (id: string) => [...edgeLinkQueryKeys.all, 'status', id] as const,
  routes: () => [...edgeLinkQueryKeys.all, 'routes'] as const,
};

// === Query Hooks ===

export function useEdgeLinkBridges(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: edgeLinkQueryKeys.list(page, pageSize),
    queryFn: () => listBridges(page, pageSize),
  });
}

export function useEdgeLinkBridge(id: string, enabled = true) {
  return useQuery({
    queryKey: edgeLinkQueryKeys.detail(id),
    queryFn: () => getBridgeById(id),
    enabled: Boolean(id && enabled),
  });
}

export function useEdgeLinkStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: edgeLinkQueryKeys.status(id),
    queryFn: () => getBridgeStatus(id),
    enabled: Boolean(id && enabled),
    refetchInterval: 5000, // Refresh status every 5 seconds
  });
}

/**
 * Fetches all bridges and flattens them into route options for selection
 */
export function useBridgeRouteOptions() {
  return useQuery({
    queryKey: edgeLinkQueryKeys.routes(),
    queryFn: async (): Promise<BridgeRouteOption[]> => {
      // Fetch all bridges (large page size to get all)
      const response = await listBridges(1, 1000);
      
      // Flatten bridges and their routes into selectable options
      const options: BridgeRouteOption[] = [];
      
      for (const bridge of response.items) {
        for (const route of bridge.routes) {
          options.push({
            bridgeId: bridge.id,
            bridgeName: bridge.name,
            bridgeType: bridge.type,
            routeId: route.id,
            routeAddressRange: route.edgeAddressRangeCidr,
            label: `${bridge.name} - ${route.edgeAddressRangeCidr}`,
          });
        }
      }
      
      return options;
    },
  });
}

// === Mutation Hooks ===

interface UseCreateBridgeOptions {
  onSuccess?: (data: EdgeLinkBridge) => void;
  onError?: (error: Error) => void;
}

export function useCreateBridge({ onSuccess, onError }: UseCreateBridgeOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: createBridge,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Bridge Created',
        description: `Successfully created EdgeLink bridge "${data.name}"`,
      });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create bridge',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

interface UseUpdateBridgeOptions {
  onSuccess?: (data: EdgeLinkBridgeDetail) => void;
  onError?: (error: Error) => void;
}

export function useUpdateBridge({ onSuccess, onError }: UseUpdateBridgeOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBridgeDto }) => updateBridge(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Bridge Updated',
        description: `Successfully updated EdgeLink bridge "${data.summary.name}"`,
      });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update bridge',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

interface UseDeleteBridgeOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useDeleteBridge({ onSuccess, onError }: UseDeleteBridgeOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: deleteBridge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Bridge Deleted',
        description: 'Successfully deleted EdgeLink bridge',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete bridge',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

// === Share Link ===

async function generateShareLink(params: { bridgeId: string; expiryDays?: number | null }): Promise<ShareLinkResponse> {
  const response = await axiosClientAdmin.post<ShareLinkResponse>(
    `/EdgeLinks/bridges/${params.bridgeId}/share-link`,
    { expiryDays: params.expiryDays === undefined ? 30 : params.expiryDays }
  );
  return response.data;
}

export function useGenerateShareLink() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: generateShareLink,
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate share link',
        variant: 'destructive',
      });
    },
  });
}

// === Public Share Status ===

async function getPublicBridgeStatus(token: string): Promise<PublicBridgeStatus> {
  const response = await publicClient.get<PublicBridgeStatus>(`/edgelink-share/${token}`);
  return response.data;
}

export function usePublicBridgeStatus(token: string | undefined) {
  return useQuery({
    queryKey: ['edgelink-share', token],
    queryFn: () => getPublicBridgeStatus(token!),
    enabled: !!token,
    refetchInterval: 15000,
  });
}

// === Route API Functions ===

async function createRoute({ bridgeId, ...data }: CreateRouteDto & { bridgeId: string }): Promise<BridgeRoute> {
  const response = await axiosClientAdmin.post<BridgeRoute>(`/EdgeLinks/bridges/${bridgeId}/routes`, data);
  return response.data;
}

async function updateRoute({ bridgeId, routeId, data }: { bridgeId: string; routeId: string; data: UpdateRouteDto }): Promise<BridgeRoute> {
  const response = await axiosClientAdmin.put<BridgeRoute>(`/EdgeLinks/bridges/${bridgeId}/routes/${routeId}`, data);
  return response.data;
}

async function deleteRoute({ bridgeId, routeId }: { bridgeId: string; routeId: string }): Promise<void> {
  await axiosClientAdmin.delete(`/EdgeLinks/bridges/${bridgeId}/routes/${routeId}`);
}

// === Route Hooks ===

interface UseCreateRouteOptions {
  onSuccess?: (data: BridgeRoute) => void;
  onError?: (error: Error) => void;
}

export function useCreateRoute({ onSuccess, onError }: UseCreateRouteOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: createRoute,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Route Created',
        description: 'Successfully created new EdgeLink route',
      });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create route',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

interface UseUpdateRouteOptions {
  onSuccess?: (data: BridgeRoute) => void;
  onError?: (error: Error) => void;
}

export function useUpdateRoute({ onSuccess, onError }: UseUpdateRouteOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ bridgeId, routeId, data }: { bridgeId: string; routeId: string; data: UpdateRouteDto }) => updateRoute({ bridgeId, routeId, data }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Route Updated',
        description: 'Successfully updated EdgeLink route',
      });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update route',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

interface UseDeleteRouteOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useDeleteRoute({ onSuccess, onError }: UseDeleteRouteOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: deleteRoute,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      toast({
        title: 'Route Deleted',
        description: 'Successfully deleted EdgeLink route',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete route',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}

// === Sites by EdgeLink Route ===

interface SiteSearchResponse {
  rows: EdgeLinkSiteDto[];
  totalRows: number;
}

interface UseSitesByBridgeOptions {
  page?: number;
  pageSize?: number;
}

/**
 * Fetches all sites and devices connected to this EdgeLink bridge using the
 * connected-assets API endpoint.
 *
 * The API returns two independent flat lists:
 * 1. Sites with a defaultRouteNamespaceId matching any of the bridge's routes
 * 2. Devices with a routeNamespaceId matching any of the bridge's routes
 *
 * Pagination applies to devices only; all matching sites are always returned.
 *
 * This hook transforms the flat response back into a grouped structure for
 * backward compatibility with the existing UI. Devices are grouped by their
 * associated site, and devices without a matching site are grouped under
 * synthetic "Unassigned" entries per customer.
 */
export function useSitesByBridge(
  bridgeId: string,
  _routes: BridgeRoute[],
  enabled = true,
  options: UseSitesByBridgeOptions = {}
) {
  const { page = 1, pageSize = 100 } = options;

  return useQuery({
    queryKey: [...edgeLinkQueryKeys.all, 'connected-assets', bridgeId, { page, pageSize }],
    queryFn: async (): Promise<EdgeLinkSiteWithDevicesDto[]> => {
      const response = await axiosClientAdmin.get<ConnectedAssetsResponse>(
        `/EdgeLinks/bridges/${bridgeId}/connected-assets`,
        { params: { page, pageSize } }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch connected assets');
      }

      const { sites, devices } = response.data;

      // Group devices by siteId
      const devicesBySiteId = new Map<string | null, typeof devices>();
      for (const device of devices || []) {
        const key = device.siteId ?? null;
        if (!devicesBySiteId.has(key)) {
          devicesBySiteId.set(key, []);
        }
        devicesBySiteId.get(key)!.push(device);
      }

      // Build result: sites with their associated devices
      const result: EdgeLinkSiteWithDevicesDto[] = (sites || []).map((site) => {
        const siteDevices = devicesBySiteId.get(site.id) || [];
        return {
          id: site.id,
          name: site.name,
          companyId: site.companyId,
          companyName: site.companyName,
          isActive: site.isActive,
          defaultRouteNamespaceId: site.defaultRouteNamespaceId,
          devices: siteDevices.map((device) => ({
            id: device.id,
            name: device.name,
            type: device.integrationName,
            isOnline: undefined,
          })),
          deviceCount: siteDevices.length,
        };
      });

      // Handle devices without a matching site - group by customer
      const unassignedDevices = devicesBySiteId.get(null) || [];
      if (unassignedDevices.length > 0) {
        // Group unassigned devices by customer
        const devicesByCustomer = new Map<string, typeof unassignedDevices>();
        for (const device of unassignedDevices) {
          if (!devicesByCustomer.has(device.customerId)) {
            devicesByCustomer.set(device.customerId, []);
          }
          devicesByCustomer.get(device.customerId)!.push(device);
        }

        // Create synthetic entries for each customer's unassigned devices
        for (const [customerId, customerDevices] of devicesByCustomer) {
          const firstDevice = customerDevices[0];
          result.push({
            // Use a clearly synthetic ID to avoid confusion with real site IDs
            id: `unassigned-${customerId}`,
            name: `${firstDevice.customerName} (Unassigned Devices)`,
            companyId: customerId,
            companyName: firstDevice.customerName,
            isActive: true,
            defaultRouteNamespaceId: undefined,
            devices: customerDevices.map((device) => ({
              id: device.id,
              name: device.name,
              type: device.integrationName,
              isOnline: undefined,
            })),
            deviceCount: customerDevices.length,
          });
        }
      }

      return result;
    },
    enabled: Boolean(bridgeId && enabled),
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Fetches all available sites (for multi-select when creating/editing bridge)
 */
export function useAllSitesForEdgeLink() {
  return useQuery({
    queryKey: [...edgeLinkQueryKeys.all, 'all-sites'],
    queryFn: async (): Promise<EdgeLinkSiteDto[]> => {
      const response = await axiosClientAdmin.get<SiteSearchResponse>(
        '/Monitoring/projects/search',
        {
          params: {
            // DISABLED DUE TO LEGACY REQUEST - need all sites for selection
            // eslint-disable-next-line custom/no-large-page-size
            PageSize: 1000,
            SortBy: 'name',
            SortDir: 'Ascending',
          },
        }
      );
      return response.data.rows || [];
    },
    staleTime: 60000, // Cache for 1 minute
  });
}

interface UseAssignSitesToRouteOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Assigns multiple sites to a specific EdgeLink route by updating their defaultRouteNamespaceId
 */
export function useAssignSitesToRoute({ onSuccess, onError }: UseAssignSitesToRouteOptions = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ siteIds, routeId }: { siteIds: string[]; routeId: string }) => {
      // We need to update each site's defaultRouteNamespaceId
      // First fetch each site to get its companyId, then update
      const updatePromises = siteIds.map(async (siteId) => {
        // Find the site's company ID from the monitoring search
        const searchRes = await axiosClientAdmin.get<SiteSearchResponse>(
          '/Monitoring/projects/search',
          {
            params: {
              PageSize: 1,
              'Filter.Operator': 'And',
              'Filter.Filters[0][field]': 'id',
              'Filter.Filters[0][action]': 'Eq',
              'Filter.Filters[0][value]': siteId,
            },
          }
        );

        const site = searchRes.data.rows?.[0];
        if (!site) throw new Error(`Site ${siteId} not found`);

        // Update the site's defaultRouteNamespaceId
        await axiosClientAdmin.post(`/Customer/${site.companyId}/project/${siteId}`, {
          defaultRouteNamespaceId: routeId,
        });
      });

      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['customer-sites'] });
      toast({
        title: 'Sites Assigned',
        description: 'Successfully assigned sites to EdgeLink route',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign sites to route',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });
}
